import ImageEditor, { ImageEditorButtons, ImageEditorFrame } from '@components/ImageEditor/ImageEditor';
import ImageSelector from '@components/ImageSelector/ImageSelector';
import useRemoteImageBlob from '@hooks/use-remote-image-blob';
import { uploadImageAsync } from '@modules/image-helpers';
import { Box, Button } from '@ui-lib';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import type { Crop } from 'react-image-crop';

type QRCodeSectionImageEditorProps = {
  /** The edited image URL for the embedded QR code image from the card object */
  editedImageUrl: string | undefined;
  isLocked?: boolean;
  onImageDeleted: () => void;
  /** When the url for image changes, will be called with local blob image URL as well as when the image has been uploaded */
  onPreviewImageUrlChange: (previewUrl: string) => void;
  /** Callback only called when the image has been saved and uploaded to storage. editedImageUrl is the uploaded image url */
  onSave: (editedImageUrl: string) => void;
};

export const QRCodeSectionImageEditor: React.FC<QRCodeSectionImageEditorProps> = ({
  editedImageUrl,
  isLocked,
  onImageDeleted,
  onPreviewImageUrlChange,
  onSave,
}) => {
  const [crop, setCrop] = useState<Crop>();

  const [saving, setSaving] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const originalImageRef = useRef<string>(undefined);
  const { imageUrl: imageReference, updateImageUrl: setImageReference } = useRemoteImageBlob(
    originalImageRef.current,
  );
  const [editedImageReference, setEditedImageReference] = useState<string>('');

  // When the editedImageUrl changes in the apollo cache, we need to update the originalImageRef in this component.
  // This should only happens as a result of saving the image after completing editing.
  useEffect(() => {
    if (editedImageUrl) {
      originalImageRef.current = editedImageUrl;
      setCrop(undefined);
    }
  }, [editedImageUrl]);

  function handleChangeImage() {
    onImageDeleted();
    setImageReference(undefined);
    originalImageRef.current = undefined;
  }

  async function uploadImage(): Promise<string> {
    const editedImageUrl = await uploadImageAsync(editedImageReference, {});

    onPreviewImageUrlChange(editedImageUrl);
    return editedImageUrl;
  }

  async function onImageSelected(localImageUrl: string) {
    setUnsavedChanges(true);
    setImageReference(localImageUrl);
    originalImageRef.current = localImageUrl;
  }

  async function handleSave() {
    setSaving(true);

    const editedImageUrl = await uploadImage();

    onSave(editedImageUrl);
    setSaving(false);
    setUnsavedChanges(false);
  }

  return (
    <Box marginTop={'medium'}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {imageReference && (
          <ImageEditorFrame>
            <ImageEditor
              disabled={isLocked ?? false}
              image={imageReference}
              onEdit={(img, crop) => {
                onPreviewImageUrlChange(img);
                setEditedImageReference(img);

                setCrop(crop);
                setUnsavedChanges(true);
              }}
              aspectRatio={1 / 1}
              crop={crop}
            />
            <ImageEditorButtons>
              <Button variant="outline" size="small" disabled={isLocked} onClick={handleChangeImage}>
                Change image
              </Button>
              <Button
                variant="solid"
                size="small"
                disabled={isLocked || !unsavedChanges}
                onClick={handleSave}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ImageEditorButtons>
          </ImageEditorFrame>
        )}
        {!imageReference && (
          <ImageSelector
            maxDimension={400}
            onSelect={(img) => {
              setCrop(undefined);
              onImageSelected(img);
            }}
          />
        )}
      </motion.div>
    </Box>
  );
};
